import { cloneDeep } from 'lodash-es';
import * as C from '../constants';
import itineraryService from '../../../services/itinerary-service';
import twentyService from '../../../services/twenty-service';
import filesService from '../../../services/files-service';
import router from '../../../router';

export default {
  /**
   * @param commit
   */
  clearState({ commit }) {
    commit(C.ITINERARY_CLEAR_STATE);
  },

  /**
   * @param commit
   * @returns {Promise<T>}
   */
  getProductTypes({ commit }) {
    commit(C.ITINERARY_PRODUCT_TYPES_PENDING);
    return itineraryService.getProductTypes()
      .then(res => {
        commit(C.ITINERARY_PRODUCT_TYPES_SUCCESS, res.data);
      }).catch(() => {
        commit(C.ITINERARY_PRODUCT_TYPES_FAILURE);
      });
  },

  /**
   * @param commit
   * @returns {Promise<T | never>}
   */
  getItineraries({ commit }) {
    commit(C.ITINERARY_OVERVIEW_PENDING);
    return itineraryService.getItineraries()
      .then(res => {
        commit(C.ITINERARY_OVERVIEW_SUCCESS, res.data);
      }).catch(() => {
        commit(C.ITINERARY_OVERVIEW_FAILURE);
      });
  },

  /**
   * @param commit
   * @param payload
   * @returns {Promise<T>}
   */
  getItinerary({ commit }, payload) {
    commit(C.ITINERARY_LOAD_GENERAL_PENDING);
    return itineraryService.getItinerary(payload)
      .then(res => {
        commit(C.ITINERARY_LOAD_GENERAL_SUCCESS, res.data);
      }).catch(() => {
        commit(C.ITINERARY_LOAD_GENERAL_FAILURE);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  createItinerary({ commit, dispatch }, payload) {
    commit(C.ITINERARY_CREATE_PENDING);
    return itineraryService.createItinerary(payload)
      .then(res => {
        commit(C.ITINERARY_CREATE_SUCCESS, { ...payload, id: res.data.id });
        router.push({ name: 'ItineraryEdit', params: { id: res.data.id } });
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'Created itinerary',
        }, { root: true });
      }).catch(err => {
        const errorCode = err.response.data.data;
        let errorMessageIdentifier = '';
        if (errorCode === 'itinerary_duplicate_violation') {
          errorMessageIdentifier = 'itinerary.errors.saveDuplicateKeyViolation';
        } else {
          errorMessageIdentifier = 'itinerary.errors.saveGeneric';
        }
        commit(C.ITINERARY_CREATE_FAILURE, errorMessageIdentifier);
      });
  },

  /**
   * @param commit
   * @param productTypeId
   * @returns {Promise<T | never>}
   */
  getCountriesByProductTypeId({ commit }, productTypeId) {
    commit(C.ITINERARY_MASTER_COUNTRIES_PENDING);
    return itineraryService.getCountriesByProductTypeId(productTypeId)
      .then(res => {
        commit(C.ITINERARY_MASTER_COUNTRIES_SUCCESS, res.data);
      }).catch(() => {
        commit(C.ITINERARY_MASTER_COUNTRIES_FAILURE);
      });
  },

  /**
   * @param commit
   * @param productTypeId
   * @returns {Promise<T>}
   */
  getCountriesWithAreasByProductTypeId({ commit }, productTypeId) {
    commit(C.ITINERARY_MASTER_COUNTRIES_PENDING);
    return itineraryService.getCountriesWithAreasByProductTypeId(productTypeId)
      .then(res => {
        commit(C.ITINERARY_MASTER_COUNTRIES_SUCCESS, res.data);
      }).catch(() => {
        commit(C.ITINERARY_MASTER_COUNTRIES_FAILURE);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {*|undefined}
   */
  updateItinerary({ commit, dispatch }, payload) {
    commit(C.ITINERARY_EDIT_PENDING);
    return itineraryService.updateItinerary(payload)
      .then(() => {
        commit(C.ITINERARY_EDIT_SUCCESS, payload);
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'Updated Itinerary',
        }, { root: true });
        window.location.reload();
      }).catch(err => {
        commit(C.ITINERARY_EDIT_FAILURE, err.response.data);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: err.response.data.data,
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param itineraryId
   * @returns {Promise<T | never>}
   */
  getItinerarySalesCountries({ commit }, itineraryId) {
    commit(C.ITINERARY_SALES_COUNTRIES_PENDING);
    return itineraryService.getItinerarySalesCountries(itineraryId)
      .then(res => {
        if (res.status === 200) {
          commit(C.ITINERARY_SALES_COUNTRIES_SUCCESS, res.data);
        } else {
          commit(C.ITINERARY_SALES_COUNTRIES_SUCCESS, []);
        }
      }).catch(err => {
        commit(C.ITINERARY_SALES_COUNTRIES_FAILURE, err.response.data);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param itineraryId
   * @param countryIds
   */
  updateItinerarySalesCountries({ commit, dispatch }, { itineraryId, countryIds }) {
    commit(C.ITINERARY_SALES_COUNTRIES_UPDATE_PENDING);
    return itineraryService.updateItinerarySalesCountries(itineraryId, countryIds)
      .then(() => {
        commit(C.ITINERARY_SALES_COUNTRIES_UPDATE_SUCCESS);
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'Updated itinerary sales countries',
        }, { root: true });
        dispatch('getItinerarySalesCountries', itineraryId);
      }).catch(err => {
        commit(C.ITINERARY_SALES_COUNTRIES_UPDATE_FAILURE, err.response.data);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: err.response.data.data,
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @returns {Promise<T>}
   */
  getElcanoTripThemes({ commit }) {
    commit(C.ITINERARY_TRIP_THEMES_PENDING);
    return twentyService.getElcanoTripThemes()
      .then(res => {
        commit(C.ITINERARY_TRIP_THEMES_SUCCESS, res.data);
      }).catch(() => {
        commit(C.ITINERARY_TRIP_THEMES_FAILURE);
      });
  },

  /**
   * @param commit
   * @param itineraryId
   * @returns {Promise<T>}
   */
  getAssignedTripThemes({ commit }, itineraryId) {
    commit(C.ITINERARY_TRIP_THEMES_ASSIGNED_PENDING);
    return itineraryService.getItineraryTripThemes(itineraryId)
      .then(res => {
        if (res.status === 200) {
          commit(C.ITINERARY_TRIP_THEMES_ASSIGNED_SUCCESS, res.data);
        } else {
          commit(C.ITINERARY_TRIP_THEMES_ASSIGNED_SUCCESS, []);
        }
      }).catch(err => {
        commit(C.ITINERARY_TRIP_THEMES_ASSIGNED_FAILURE, err.response.data);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param itineraryId
   * @param contentIds
   * @returns {Promise<T>}
   */
  updateAssignedTripThemes({ commit, dispatch }, { itineraryId, contentIds }) {
    commit(C.ITINERARY_TRIP_THEMES_UPDATE_PENDING);
    return itineraryService.updateItineraryTripThemes(itineraryId, contentIds)
      .then(() => {
        commit(C.ITINERARY_TRIP_THEMES_UPDATE_SUCCESS);
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'Updated trip themes',
        }, { root: true });
        dispatch('getAssignedTripThemes', itineraryId);
      }).catch(() => {
        commit(C.ITINERARY_TRIP_THEMES_UPDATE_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'Failed to update trip themes',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param itineraryId
   * @returns {Promise<T>}
   */
  getDayProgram({ commit }, itineraryId) {
    commit(C.ITINERARY_DAY_PROGRAM_PENDING);
    return itineraryService.getDayProgram(itineraryId)
      .then(res => {
        if (res.status === 200) {
          commit(C.ITINERARY_DAY_PROGRAM_SUCCESS, res.data);
        } else if (res.status === 204) {
          commit(C.ITINERARY_DAY_PROGRAM_NO_CONTENT);
        }
      }).catch(() => {
        commit(C.ITINERARY_DAY_PROGRAM_FAILURE);
      });
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  addDayProgramEntry({ commit, state, dispatch }, payload) {
    commit(C.ITINERARY_DAY_PROGRAM_ADD_PENDING);
    const dayProgram = cloneDeep(state.dayProgram.items);
    return itineraryService.addDayProgramEntry(payload)
      .then(res => {
        const entry = Object.assign({});
        entry.description = '';
        entry.id = res.data.id;
        dayProgram.push(entry);
        commit(C.ITINERARY_DAY_PROGRAM_ADD_SUCCESS, dayProgram);
        dispatch('getDayProgram', payload.itineraryId);
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'Added day to day program',
        }, { root: true });
      }).catch(() => {
        commit(C.ITINERARY_DAY_PROGRAM_ADD_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'Failed to add day to day program',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  duplicateDayProgramEntry({ commit, dispatch }, payload) {
    commit(C.ITINERARY_DAY_PROGRAM_DUPLICATE_DAY_PENDING);
    return itineraryService.duplicateDayProgramEntry(payload)
      .then(() => {
        commit(C.ITINERARY_DAY_PROGRAM_DUPLICATE_DAY_SUCCESS);
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'Duplicated day to day program',
        }, { root: true });
        window.location.reload();
      }).catch(() => {
        commit(C.ITINERARY_DAY_PROGRAM_DUPLICATE_DAY_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'Failed to duplicate day to day program',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  deleteDayProgramEntry({ commit, state, dispatch }, payload) {
    commit(C.ITINERARY_DAY_PROGRAM_DELETE_DAY_PENDING);
    return itineraryService.deleteDayProgramEntry(payload)
      .then(() => {
        const index = state.dayProgram.items.findIndex(day => day.id === payload.dayProgramId);
        commit(C.ITINERARY_DAY_PROGRAM_DELETE_DAY_SUCCESS, index);
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'Deleted day from day program',
        }, { root: true });
      }).catch(() => {
        commit(C.ITINERARY_DAY_PROGRAM_DELETE_DAY_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'Failed to delete day from day program',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  updateDayProgram({ commit, dispatch }, payload) {
    commit(C.ITINERARY_DAY_PROGRAM_UPDATE_PENDING);
    return itineraryService.updateDayProgram(payload)
      .then(() => {
        commit(C.ITINERARY_DAY_PROGRAM_UPDATE_SUCCESS, payload);
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'Updated dayprogram',
        }, { root: true });
      }).catch(() => {
        commit(C.ITINERARY_DAY_PROGRAM_UPDATE_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'Failed to update dayprogram',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param payload
   */
  setLocationDayProgram({ commit }, payload) {
    commit(C.ITINERARY_DAY_PROGRAM_LOCATION_SET, payload);
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  updateDayProgramLocation({ commit, dispatch }, payload) {
    commit(C.ITINERARY_DAY_PROGRAM_LOCATION_SAVE_PENDING);
    return itineraryService.updateDayProgramLocation(payload)
      .then(() => {
        dispatch('setDayProgramLocationDataOnUpdate', {
          data: payload.data,
        });
        commit(C.ITINERARY_DAY_PROGRAM_LOCATION_SAVE_SUCCESS);
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'Updated itinerary location',
        }, { root: true });
      }).catch(() => {
        commit(C.ITINERARY_DAY_PROGRAM_LOCATION_SAVE_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'Failed to update itinerary location',
        }, { root: true });
        throw new Error();
      });
  },

  /**
   * @param commit
   * @param state
   * @param payload
   */
  setDayProgramLocationDataOnUpdate({ commit, state }, payload) {
    const dayProgramList = [ ...state.dayProgram.items ];
    const dayProgramIndex = dayProgramList.findIndex(
      dayprogram => dayprogram.id === payload.data.dayProgramId,
    );

    const newPayload = {
      dayProgramIndex,
      countryId: null,
      country: null,
      areaId: null,
      area: null,
    };

    if (payload.data.areaId) {
      const countriesWithAreas = [ ...state.masterCountries.items ];
      const country = countriesWithAreas.filter(
        countryFilter => countryFilter.id === payload.data.countryId,
      );
      const area = country[0].areas.filter(areaFilter => areaFilter.id === payload.data.areaId);

      newPayload.countryId = country[0].id;
      newPayload.country = country[0].alias;
      newPayload.areaId = area[0].id;
      newPayload.area = area[0].name;
    }
    commit(C.ITINERARY_DAY_PROGRAM_LOCATION_UPDATE_DATA, newPayload);
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   */
  async fetchDayProgramHotelData({ commit, dispatch }, payload) {
    commit(C.ITINERARY_DAY_PROGRAM_HOTEL_PENDING, payload.data);
    await dispatch('fetchHotelsByAreaId', { areaId: payload.data.areaId });
    await dispatch('fetchHotelsByDayProgramId', { itineraryId: payload.itineraryId, dayProgramId: payload.data.id });
    commit(C.ITINERARY_DAY_PROGRAM_HOTEL_CLEAR_LOADING);
  },

  /**
   * @param commit
   * @param areaId
   * @returns {PromiseLike<T> | Promise<T> | *}
   */
  fetchHotelsByAreaId({ commit }, { areaId }) {
    return twentyService.getCountriesByAreaIds([ areaId ]).then(res => {
      commit(C.ITINERARY_DAY_PROGRAM_SET_AVAILABLE_HOTELS, res.data);
    });
  },

  /**
   * @param commit
   * @param itineraryId
   * @param dayProgramId
   * @returns {PromiseLike<T> | Promise<T> | *}
   */
  fetchHotelsByDayProgramId({ commit }, { itineraryId, dayProgramId }) {
    return itineraryService.getDayProgramHotels(itineraryId, dayProgramId).then(res => {
      if (res.data) {
        commit(C.ITINERARY_DAY_PROGRAM_SET_SELECTED_HOTELS, res.data);
      }
    });
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   * @param itineraryId
   * @param dayProgramId
   * @param siteIds
   * @returns {Promise<T>}
   */
  updateDayProgramSites({ commit, state, dispatch }, { itineraryId, dayProgramId, siteIds }) {
    commit(C.ITINERARY_DAY_PROGRAM_HOTEL_SET_LOADING);
    return itineraryService.updateDayProgramSites(itineraryId, dayProgramId, siteIds)
      .then(() => {
        const dayProgramList = [ ...state.dayProgram.items ];
        const dayProgramIndex = dayProgramList.findIndex(
          dayprogram => dayprogram.id === dayProgramId,
        );
        commit(C.ITINERARY_DAY_PROGRAM_HOTEL_UPDATE_SUCCESS, {
          dayProgramIndex,
          hotelCount: siteIds.length,
        });
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'Dayprogram sites updated',
        }, { root: true });
      }).catch(() => {
        commit(C.ITINERARY_DAY_PROGRAM_HOTEL_CLEAR_LOADING);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'An error occurred white attempting to save hotels for day program',
        }, { root: true });
        throw new Error();
      });
  },

  /**
   * @param commit
   * @param payload
   * @returns {Promise<void>}
   */
  async fetchDayProgramExtraData({ commit }, payload) {
    commit(C.ITINERARY_DAY_PROGRAM_EXTRAS_PENDING, payload.data);

    return twentyService.getExtrasByCountryId(payload.data.countryId).then(res => {
      const mappedExtras = res.data.extras.map(e => {
        const result = payload.data.extras.filter(s => s.id === e.id)[0] || null;
        // The backend has a universal response for extras, instead of messing with the response,
        // I simply set the type here.
        const extra = { ...e, type: 'none' };
        return { ...extra, ...result };
      });
      commit(C.ITINERARY_DAY_PROGRAM_SET_AVAILABLE_EXTRAS, { extras: mappedExtras, config: res.data.config });
    }).catch(() => {
      commit(C.ITINERARY_DAY_PROGRAM_EXTRAS_CLEAR_LOADING);
    });
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   * @param itineraryId
   * @param dayProgramId
   * @param mappings
   * @returns {Promise<T>}
   */
  updateDayProgramExtras({ commit, state, dispatch }, { itineraryId, dayProgramId, extras }) {
    commit(C.ITINERARY_DAY_PROGRAM_EXTRAS_SET_LOADING);

    const dayProgramList = cloneDeep(state.dayProgram.items);
    const dayProgramIndex = dayProgramList.findIndex(
      dayprogram => dayprogram.id === dayProgramId,
    );

    return itineraryService.updateDayProgramExtras(itineraryId, dayProgramId, extras)
      .then(() => {
        commit(C.ITINERARY_DAY_PROGRAM_EXTRAS_UPDATE_SUCCESS, {
          dayProgramIndex,
          extras,
        });

        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'Day program extras has been updated',
        }, { root: true });
      }).catch(() => {
        commit(C.ITINERARY_DAY_PROGRAM_EXTRAS_CLEAR_LOADING);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'An error occurred while attempting to update extras for day program',
        }, { root: true });
        throw new Error();
      });
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   * @param itineraryId
   * @param dayProgramId
   * @param extras
   * @returns {Promise<void>}
   */
  updateDayProgramExtraAdditionals({ commit, dispatch }, { itineraryId, extras }) {
    commit(C.ITINERARY_DAY_PROGRAM_EXTRAS_SET_LOADING);

    return itineraryService.updateDayProgramExtraAdditionals(itineraryId, extras)
      .then(() => {
        commit(C.ITINERARY_DAY_PROGRAM_EXTRAS_UPDATE_SUCCESS, {
          extras,
        });

        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'Day program extras additionals has been updated',
        }, { root: true });
      }).catch(() => {
        commit(C.ITINERARY_DAY_PROGRAM_EXTRAS_CLEAR_LOADING);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'An error occurred while attempting to update extras additionals for day program',
        }, { root: true });
        throw new Error();
      });
  },

  /**
   * @param commit
   */
  getMealPlanTypes({ commit }) {
    commit(C.ITINERARY_MEAL_PLAN_TYPES_PENDING);
    itineraryService.getMealPlanTypes()
      .then(res => {
        commit(C.ITINERARY_MEAL_PLAN_TYPES_SUCCESS, res.data);
      }).catch(() => {
        commit(C.ITINERARY_MEAL_PLAN_TYPES_FAILURE);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  getDayProgramMealPlans({ commit, dispatch }, payload) {
    commit(C.ITINERARY_DAY_PROGRAM_MEAL_PLANS_PENDING, payload.data);
    return itineraryService.getDayProgramMealPlans(payload.itineraryId, payload.data.id)
      .then(res => {
        commit(C.ITINERARY_DAY_PROGRAM_MEAL_PLANS_SUCCESS, res.data || []);
      }).catch(() => {
        commit(C.ITINERARY_DAY_PROGRAM_MEAL_PLANS_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'An error occurred while fetching meal plan mappings',
        }, { root: true });
        throw new Error();
      });
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   * @param itineraryId
   * @param dayProgramId
   * @param mappings
   * @returns {Promise<T>}
   */
  updateDayProgramMealPlans({ commit, state, dispatch }, { itineraryId, dayProgramId, mappings }) {
    commit(C.ITINERARY_DAY_PROGRAM_MEAL_PLANS_SET_LOADING);
    return itineraryService.updateDayProgramMealPlans(itineraryId, dayProgramId, mappings)
      .then(() => {
        const dayProgramList = [ ...state.dayProgram.items ];
        const dayProgramIndex = dayProgramList.findIndex(
          dayprogram => dayprogram.id === dayProgramId,
        );
        commit(C.ITINERARY_DAY_PROGRAM_MEAL_PLANS_UPDATE_SUCCESS, {
          dayProgramIndex,
          mealplanCount: mappings.length,
        });

        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'Day program meal plans updated',
        }, { root: true });
      }).catch(() => {
        commit(C.ITINERARY_DAY_PROGRAM_MEAL_PLANS_CLEAR_LOADING);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'Failed to update meal plans for day program',
        }, { root: true });
        throw new Error();
      });
  },

  /**
   * @param commit
   * @param itineraryId
   * @returns {Promise<T | never>}
   */
  getItineraryDomains({ commit }, itineraryId) {
    commit(C.ITINERARY_DOMAINS_PENDING);
    return itineraryService.getItineraryDomains(itineraryId)
      .then(res => {
        if (res.status === 200) {
          commit(C.ITINERARY_DOMAINS_SUCCESS, res.data);
          return res.data;
        }
        commit(C.ITINERARY_DOMAINS_NO_CONTENT);
        return [];
      }).catch(() => {
        commit(C.ITINERARY_DOMAINS_FAILURE);
      });
  },

  /**
   * @param commit
   * @param payload
   */
  selectItineraryDomain({ commit }, payload) {
    commit(C.ITINERARY_DOMAIN_SELECT, payload);
  },

  /**
   * @param commit
   * @param payload
   */
  deselectItineraryDomain({ commit }) {
    commit(C.ITINERARY_DOMAIN_DESELECT);
  },

  /**
   * @param commit
   * @param dispatch
   * @param itineraryId
   * @param domainIds
   */
  addItineraryDomains({ commit, dispatch }, { itineraryId, domainIds }) {
    commit(C.ITINERARY_DOMAINS_PENDING);
    const itineraryDomains = domainIds.map(domainId => ({
      itineraryId,
      domainId,
    }));
    return itineraryService.addItineraryDomains(itineraryId, itineraryDomains)
      .then(() => {
        dispatch('getItineraryDomains', itineraryId);
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'Itinerary domains added',
        }, { root: true });
      }).catch(() => {
        commit(C.ITINERARY_DOMAINS_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'Failed to add itinerary domains',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param itineraryId
   * @param domains
   * @param domainIds
   */
  updateItineraryDomains({ commit, dispatch }, { itineraryId, itineraryDomains }) {
    commit(C.ITINERARY_DOMAINS_PENDING);
    return itineraryService.updateItineraryDomains(itineraryId, itineraryDomains)
      .then(() => {
        dispatch('getItineraryDomains', itineraryId);
        commit(C.ITINERARY_DOMAINS_SUCCESS, itineraryDomains);
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'Itinerary domains updated',
        }, { root: true });
      }).catch(() => {
        commit(C.ITINERARY_DOMAINS_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'Failed to update itinerary domains',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param dayProgramId
   * @param id
   * @returns {*}
   */
  getItineraryDayProgramImages({ commit, dispatch }, { dayProgramId }) {
    commit(C.ITINERARY_DAY_PROGRAM_IMAGES_PENDING);
    return filesService.getItineraryDayProgramImages(dayProgramId)
      .then(res => {
        commit(C.ITINERARY_DAY_PROGRAM_IMAGES_SUCCESS, res.data);
      })
      .catch(() => {
        commit(C.ITINERARY_DAY_PROGRAM_IMAGES_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'Failed to load itinerary day program images',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param dayProgramId
   * @param images
   * @returns {Promise<T | never>}
   */
  uploadItineraryDayProgramImages({ commit, dispatch }, { dayProgramId, images }) {
    commit(C.ITINERARY_DAY_PROGRAM_IMAGES_SAVE_PENDING);
    const formData = new FormData();
    images.forEach(image => {
      formData.append('files[]', image);
    });
    return filesService.uploadItineraryDayProgramImages(dayProgramId, formData)
      .then(res => {
        if (res.status === 204) {
          commit(C.ITINERARY_DAY_PROGRAM_IMAGES_SAVE_SUCCESS);
          dispatch('snackbar/show', {
            identifier: 'success',
            message: 'Itinerary day program images saved successfully',
          }, { root: true });
        } else {
          dispatch('snackbar/show', {
            identifier: 'error',
            message: 'Failed to upload day program images',
          }, { root: true });
        }
        dispatch('getItineraryDayProgramImages', { dayProgramId });
      })
      .catch(() => {
        commit(C.ITINERARY_DAY_PROGRAM_IMAGES_SAVE_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'Failed to upload day program images',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param dayProgramId
   * @param fileIds
   * @returns {Promise<T | never>}
   */
  updateItineraryDayProgramImages({ commit, dispatch }, { dayProgramId, fileIds }) {
    commit(C.ITINERARY_DAY_PROGRAM_IMAGES_SAVE_PENDING);
    return filesService.updateImageOrder(fileIds)
      .then(res => {
        if (res.status === 204) {
          commit(C.ITINERARY_DAY_PROGRAM_IMAGES_SAVE_SUCCESS);
          dispatch('snackbar/show', {
            identifier: 'success',
            message: 'Itinerary day program images saved successfully',
          }, { root: true });
        } else {
          dispatch('snackbar/show', {
            identifier: 'error',
            message: 'Failed to save itinerary day program images',
          }, { root: true });
        }
        dispatch('getItineraryDayProgramImages', { dayProgramId });
      })
      .catch(() => {
        commit(C.ITINERARY_DAY_PROGRAM_IMAGES_SAVE_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'Failed to save itinerary day program images',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param dayProgramId
   * @param image
   * @param imageId
   * @returns {Promise<T | never>}
   */
  deleteItineraryDayProgramImage({ commit, dispatch }, { dayProgramId, image }) {
    commit(C.ITINERARY_DAY_PROGRAM_IMAGES_SAVE_PENDING);
    return filesService.deleteImage(image.id)
      .then(res => {
        if (res.status === 204) {
          commit(C.ITINERARY_DAY_PROGRAM_IMAGES_SAVE_SUCCESS);
          dispatch('snackbar/show', {
            identifier: 'success',
            message: 'Itinerary day program image deleted successfully',
          }, { root: true });
        } else {
          dispatch('snackbar/show', {
            identifier: 'error',
            message: 'Failed to delete itinerary day program image',
          }, { root: true });
        }
        dispatch('getItineraryDayProgramImages', { dayProgramId });
      })
      .catch(() => {
        commit(C.ITINERARY_DAY_PROGRAM_IMAGES_SAVE_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'Failed to delete itinerary day program image',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  updateSeoTeaserEntry({ commit, dispatch }, payload) {
    commit(C.ITINERARY_TEASER_UPDATE_PENDING);
    return itineraryService.updateSeoTeaserEntry(payload)
      .then(() => {
        commit(C.ITINERARY_TEASER_UPDATE_SUCCESS, payload);
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'Itinerary teasers updated',
        }, { root: true });
      }).catch(() => {
        commit(C.ITINERARY_TEASER_UPDATE_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'Failed to update itinerary teasers',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param itineraryId
   * @returns {Promise<T>}
   */
  getSeoTeaserEntries({ commit }, itineraryId) {
    commit(C.ITINERARY_TEASER_PENDING);
    return itineraryService.getSeoTeaserEntries(itineraryId)
      .then(res => {
        if (res.status === 200) {
          commit(C.ITINERARY_TEASER_SUCCESS, res.data);
        } else {
          commit(C.ITINERARY_TEASER_SUCCESS, []);
        }
      }).catch(() => {
        commit(C.ITINERARY_TEASER_FAILURE);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  updateIncludeEntries({ commit, dispatch }, payload) {
    commit(C.ITINERARY_INCLUDE_UPDATE_PENDING);
    return itineraryService.updateIncludeEntries(payload, payload.includeNot)
      .then(() => {
        commit(C.ITINERARY_INCLUDE_UPDATE_SUCCESS, payload);
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'Itinerary includes updated',
        }, { root: true });
      }).catch(() => {
        commit(C.ITINERARY_INCLUDE_UPDATE_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'Failed to update itinerary includes',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param itineraryId
   * @returns {Promise<T>}
   */
  getIncludeEntries({ commit, dispatch }, payload) {
    commit(C.ITINERARY_INCLUDE_PENDING);
    return itineraryService.getIncludeEntries(payload.itineraryId, payload.includeNot)
      .then(res => {
        const refactoredList = [];
        res.data.forEach(obj => {
          const refactored = {};
          obj.domains.forEach(domain => {
            refactored[`${domain.domainId}`] = domain;
          });
          refactoredList.push({
            includeCategoryId: obj.includeCategoryId,
            text: obj.text,
            headline: obj.headline,
            domains: refactored,
            priority: obj.priority,
            id: obj.id,
            notIncluded: obj.notIncluded,
          });
        });
        if (res.status === 200) {
          if (refactoredList.length) {
            commit(C.ITINERARY_INCLUDE_SUCCESS, refactoredList);
          } else {
            commit(C.ITINERARY_INCLUDE_NO_CONTENT);
            dispatch('snackbar/show', {
              identifier: 'warning',
              message: 'No content on itinerary',
            }, { root: true });
          }
        } else {
          throw new Error();
        }
      }).catch(() => {
        commit(C.ITINERARY_INCLUDE_FAILURE);
      });
  },

  /**
   * @param commit
   * @returns {Promise<T | never>}
   */
  getIncludeCategories({ commit }, itineraryId) {
    commit(C.ITINERARY_INCLUDE_CATEGORIES_PENDING);
    return itineraryService.getIncludeCategories(itineraryId)
      .then(res => {
        commit(C.ITINERARY_INCLUDE_CATEGORIES_SUCCESS, res.data);
      }).catch(() => {
        commit(C.ITINERARY_INCLUDE_CATEGORIES_FAILURE);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param itineraryId
   * @param images
   * @returns {Promise<T | never>}
   */
  uploadBannerImage({ commit, dispatch }, { itineraryId, images }) {
    commit(C.ITINERARY_BANNER_IMAGES_SAVE_PENDING);
    const formData = new FormData();
    images.forEach(image => {
      formData.append('files[]', image);
    });
    return filesService.uploadItineraryBannerImages(itineraryId, formData)
      .then(res => {
        if (res.status === 204) {
          commit(C.ITINERARY_BANNER_IMAGES_SAVE_SUCCESS);
          dispatch('snackbar/show', {
            identifier: 'success',
            message: 'Banner images saved',
          }, { root: true });
        } else {
          dispatch('snackbar/show', {
            identifier: 'error',
            message: 'Banner images save error',
          }, { root: true });
        }
        dispatch('getItineraryBannerImages', itineraryId);
      })
      .catch(() => {
        commit(C.ITINERARY_BANNER_IMAGES_SAVE_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'Banner images save error',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param itineraryId
   * @param fileIds
   * @returns {Promise<T | never>}
   */
  updateItineraryBannerImages({ commit, dispatch }, { itineraryId, fileIds }) {
    commit(C.ITINERARY_BANNER_IMAGES_SAVE_PENDING);
    return filesService.updateImageOrder(fileIds)
      .then(res => {
        if (res.status === 204) {
          commit(C.ITINERARY_BANNER_IMAGES_SAVE_SUCCESS);
          dispatch('snackbar/show', {
            identifier: 'success',
            message: 'Banner images saved',
          }, { root: true });
        } else {
          dispatch('snackbar/show', {
            identifier: 'error',
            message: 'Banner images save error',
          }, { root: true });
        }
        dispatch('getItineraryBannerImages', itineraryId);
      })
      .catch(() => {
        commit(C.ITINERARY_BANNER_IMAGES_SAVE_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'Banner images save error',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param dayProgramId
   * @param image
   * @param imageId
   * @returns {Promise<T | never>}
   */
  deleteItineraryBannerImage({ commit, dispatch }, { itineraryId, image }) {
    commit(C.ITINERARY_BANNER_IMAGES_SAVE_PENDING);
    return filesService.deleteImage(image.id)
      .then(res => {
        if (res.status === 204) {
          commit(C.ITINERARY_BANNER_IMAGES_SAVE_SUCCESS);
          dispatch('snackbar/show', {
            identifier: 'success',
            message: 'Banner images deleted successfully',
          }, { root: true });
        } else {
          dispatch('snackbar/show', {
            identifier: 'error',
            message: 'Banner images delete error',
          }, { root: true });
        }
        dispatch('getItineraryBannerImages', itineraryId);
      })
      .catch(() => {
        commit(C.ITINERARY_BANNER_IMAGES_SAVE_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'Banner images delete error',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param itineraryId
   * @returns {Promise<T | never>}
   */
  getItineraryBannerImages({ commit, dispatch }, itineraryId) {
    commit(C.ITINERARY_BANNER_IMAGES_LOAD_PENDING);
    return filesService.getItineraryBannerImages(itineraryId)
      .then(res => {
        if (res.status === 200) {
          commit(C.ITINERARY_BANNER_IMAGES_LOAD_SUCCESS, res.data);
          dispatch('snackbar/show', {
            identifier: 'success',
            message: 'Banner images loaded successfully',
          }, { root: true });
        } else if (res.status === 204) {
          commit(C.ITINERARY_BANNER_IMAGES_LOAD_SUCCESS, []);
        } else {
          commit(C.ITINERARY_BANNER_IMAGES_LOAD_SUCCESS, []);
          dispatch('snackbar/show', {
            identifier: 'warning',
            message: 'No banner images found',
          }, { root: true });
        }
      })
      .catch(() => {
        commit(C.ITINERARY_BANNER_IMAGES_LOAD_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'Failed to load banner images',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param itineraryId
   * @param images
   * @returns {Promise<T | never>}
   */
  uploadMapImage({ commit, dispatch }, { itineraryId, images }) {
    commit(C.ITINERARY_MAP_IMAGES_SAVE_PENDING);
    const formData = new FormData();
    images.forEach(image => {
      formData.append('files[]', image);
    });
    return filesService.uploadItineraryMapImages(itineraryId, formData)
      .then(res => {
        if (res.status === 204) {
          commit(C.ITINERARY_MAP_IMAGES_SAVE_SUCCESS);
          dispatch('snackbar/show', {
            identifier: 'success',
            message: 'Map images saved',
          }, { root: true });
        } else {
          dispatch('snackbar/show', {
            identifier: 'error',
            message: 'Map images save error',
          }, { root: true });
        }
        dispatch('getItineraryMapImages', itineraryId);
      }).catch(() => {
        commit(C.ITINERARY_MAP_IMAGES_SAVE_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'Map images save error',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param itineraryId
   * @param fileIds
   * @returns {Promise<T | never>}
   */
  updateItineraryMapImages({ commit, dispatch }, { itineraryId, fileIds }) {
    commit(C.ITINERARY_MAP_IMAGES_SAVE_PENDING);
    return filesService.updateImageOrder(fileIds)
      .then(res => {
        if (res.status === 204) {
          commit(C.ITINERARY_MAP_IMAGES_SAVE_SUCCESS);
          dispatch('snackbar/show', {
            identifier: 'success',
            message: 'Map images saved',
          }, { root: true });
        } else {
          dispatch('snackbar/show', {
            identifier: 'error',
            message: 'Map images save error',
          }, { root: true });
        }
        dispatch('getItineraryMapImages', itineraryId);
      }).catch(() => {
        commit(C.ITINERARY_MAP_IMAGES_SAVE_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'Map images save error',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param dayProgramId
   * @param image
   * @param imageId
   * @returns {Promise<T | never>}
   */
  deleteItineraryMapImage({ commit, dispatch }, { itineraryId, image }) {
    commit(C.ITINERARY_MAP_IMAGES_SAVE_PENDING);
    return filesService.deleteImage(image.id)
      .then(res => {
        if (res.status === 204) {
          commit(C.ITINERARY_MAP_IMAGES_SAVE_SUCCESS);
          dispatch('snackbar/show', {
            identifier: 'success',
            message: 'Map images deleted',
          }, { root: true });
        } else {
          dispatch('snackbar/show', {
            identifier: 'error',
            message: 'Map images delete error',
          }, { root: true });
        }
        dispatch('getItineraryMapImages', itineraryId);
      })
      .catch(() => {
        commit(C.ITINERARY_MAP_IMAGES_SAVE_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'itinerary_map_imagesMap images delete error_delete_error',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param itineraryId
   * @returns {Promise<T | never>}
   */
  getItineraryMapImages({ commit, dispatch }, itineraryId) {
    commit(C.ITINERARY_MAP_IMAGES_LOAD_PENDING);
    return filesService.getItineraryMapImages(itineraryId)
      .then(res => {
        if (res.status === 200) {
          commit(C.ITINERARY_MAP_IMAGES_LOAD_SUCCESS, res.data);
          dispatch('snackbar/show', {
            identifier: 'success',
            message: 'Map images loaded',
          }, { root: true });
        } else if (res.status === 204) {
          commit(C.ITINERARY_MAP_IMAGES_LOAD_SUCCESS, []);
        } else {
          commit(C.ITINERARY_MAP_IMAGES_LOAD_SUCCESS, []);
          dispatch('snackbar/show', {
            identifier: 'error',
            message: 'Map images load error',
          }, { root: true });
        }
      }).catch(() => {
        commit(C.ITINERARY_MAP_IMAGES_LOAD_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'Map images load error',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T | never>}
   */
  syncItineraryPriceIntervals({ commit, dispatch }, payload) {
    const { domainId, itineraryId, priceIntervals } = payload;
    commit(C.ITINERARY_PRICE_INTERVAL_SYNC_PENDING);
    return itineraryService.syncItineraryPriceIntervals(itineraryId, domainId, priceIntervals)
      .then(res => {
        if (res.status === 200) {
          commit(C.ITINERARY_PRICE_INTERVAL_SYNC_SUCCESS, res.data);
          dispatch('snackbar/show', {
            identifier: 'success',
            message: 'Successfully synced price intervals',
          }, { root: true });
        }
      }).catch(() => {
        commit(C.ITINERARY_PRICE_INTERVAL_SYNC_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'Failed to sync price intervals',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param payload
   * @returns {Promise<T | never>}
   */
  getItineraryPriceIntervals({ commit }, payload) {
    commit(C.ITINERARY_PRICE_INTERVALS_GET_PENDING);
    const { itineraryId, domainId } = payload;
    return itineraryService.getItineraryPriceIntervals(itineraryId, domainId)
      .then(res => {
        if (res.status === 200) {
          commit(C.ITINERARY_PRICE_INTERVALS_GET_SUCCESS, res.data);
        } else if (res.status === 204) {
          commit(C.ITINERARY_PRICE_INTERVALS_GET_NO_CONTENT);
        }
      }).catch(() => {
        commit(C.ITINERARY_PRICE_INTERVALS_GET_FAILURE);
      });
  },

  getItineraryDayProgram({ commit }, itineraryId) {
    commit(C.ITINERARY_DAY_PROGRAM_PRICES_PENDING);
    itineraryService.getDayProgram(itineraryId).then(res => {
      commit(C.ITINERARY_DAY_PROGRAM_PRICES_SUCCESS, res.data);
    }).catch(() => {
      commit(C.ITINERARY_DAY_PROGRAM_PRICES_FAILURE);
    });
  },

  /**
   * @param commit
   * @param payload
   * @param priceIntervalId
   * @param dispatch
   * @returns {Promise<T | never>}
   */
  syncItineraryPriceSettings({ commit, dispatch }, payload) {
    commit(C.ITINERARY_PRICE_SETTINGS_SYNC_PENDING);
    return itineraryService.syncItineraryPriceSettings(payload).then(res => {
      if (res.status === 200) {
        commit(C.ITINERARY_PRICE_SETTINGS_SYNC_SUCCESS, res.data);
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'Successfully calculated and synchronized.',
        }, { root: true });
      } else {
        throw new Error();
      }
    }).catch(e => {
      commit(C.ITINERARY_PRICE_SETTINGS_SYNC_FAILURE);
      dispatch('snackbar/show', {
        identifier: 'error',
        message: `Failed to calculate and synchronize. - ${e.response.data.data}`,
      }, { root: true });
    });
  },

  /**
   * @param commit
   * @param dispatch
   * @param priceIntervalId
   * @returns {Promise<T | never>}
   */
  getItineraryPriceSettings({ commit, dispatch }, priceIntervalId) {
    commit(C.ITINERARY_PRICE_SETTINGS_GET_PENDING);
    return itineraryService.getItineraryPriceSettings(priceIntervalId)
      .then(res => {
        if (res.status === 200) {
          commit(C.ITINERARY_PRICE_SETTINGS_GET_SUCCESS, res.data);
          dispatch('snackbar/show', {
            identifier: 'success',
            message: 'Successfully fetched prices',
          }, { root: true });
        } else if (res.status === 204) {
          commit(C.ITINERARY_PRICE_SETTINGS_GET_NO_CONTENT);
          dispatch('snackbar/show', {
            identifier: 'warning',
            message: 'No prices were found for given interval',
          }, { root: true });
        }
      }).catch(() => {
        commit(C.ITINERARY_PRICE_SETTINGS_GET_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'Failed to fetch prices',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param payload
   * @param priceIntervalId
   * @param dispatch
   * @returns {Promise<T | never>}
   */
  copyItineraryPriceSettings({ commit, dispatch }, payload) {
    commit(C.ITINERARY_PRICE_SETTINGS_COPY_PENDING);
    return itineraryService.copyItineraryPriceSettings(payload.priceIntervalId).then(res => {
      if (res.status === 200) {
        commit(C.ITINERARY_PRICE_SETTINGS_COPY_SUCCESS);
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'itinerary_price_setting_copy_success',
        }, { root: true });
        dispatch('getItineraryPriceIntervals', payload);
      } else {
        throw new Error();
      }
    }).catch(() => {
      commit(C.ITINERARY_PRICE_SETTINGS_COPY_FAILURE);
      dispatch('snackbar/show', {
        identifier: 'error',
        message: 'itinerary_price_setting_copy_error',
      }, { root: true });
    });
  },

  /**
   * @param commit
   * @param payload
   * @param priceIntervalId
   * @param dispatch
   * @returns {Promise<T | never>}
   */
  cloneItinerary({ commit, dispatch }, payload) {
    commit(C.ITINERARY_CLONE_PENDING);
    return itineraryService.cloneItinerary(payload.itineraryId).then(res => {
      if (res.status === 200) {
        commit(C.ITINERARY_CLONE_SUCCESS);
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'Successfully cloned itinerary',
        }, { root: true });
        location.href = `/itinerary/edit/${res.data.id}/general`;
      } else {
        throw new Error();
      }
    }).catch(() => {
      commit(C.ITINERARY_CLONE_FAILURE);
      dispatch('snackbar/show', {
        identifier: 'error',
        message: 'Failed to clone itinerary',
      }, { root: true });
    });
  },

  /**
   * @param commit
   * @param dispatch
   * @param itineraryId
   * @returns {Promise<T | never>}
   */
  getItineraryMatrixAges({ commit, dispatch }, domainId) {
    commit(C.ITINERARY_MATRIX_AGES_GET_PENDING);
    return itineraryService.getItineraryMatrixAges(domainId).then(res => {
      if (res.status === 200) {
        commit(C.ITINERARY_MATRIX_AGES_GET_SUCCESS, res.data);
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'Successfully fetched matrix ages',
        }, { root: true });
      } else if (res.status === 204) {
        commit(C.ITINERARY_MATRIX_AGES_GET_NO_CONTENT);
        dispatch('snackbar/show', {
          identifier: 'warning',
          message: 'Matrix ages returned no content',
        }, { root: true });
      }
    }).catch(() => {
      commit(C.ITINERARY_MATRIX_AGES_GET_FAILURE);
      dispatch('snackbar/show', {
        identifier: 'error',
        message: 'Failed to fetch matrix ages',
      }, { root: true });
    });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T | never>}
   */
  getItineraryMatrixPrices({ commit, dispatch }, { itineraryId, domainId }) {
    commit(C.ITINERARY_MATRIX_PRICES_GET_PENDING);
    return itineraryService.getItineraryMatrixPrices({ itineraryId, domainId }).then(res => {
      if (res.status === 200) {
        commit(C.ITINERARY_MATRIX_PRICES_GET_SUCCESS, res.data);
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'Successfully fetched matrix prices',
        }, { root: true });
      }
    }).catch(() => {
      commit(C.ITINERARY_MATRIX_PRICES_GET_FAILURE);
      dispatch('snackbar/show', {
        identifier: 'error',
        message: 'Failed to fetch matrix prices',
      }, { root: true });
    });
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   * @param payload
   */
  calclutatePriceIntervals({ commit, state, dispatch }) {
    commit(C.ITINERARY_MATRIX_CALC_PRICES_PENDING);
    const pricesIntervals = state.priceIntervals.items;

    const requests = pricesIntervals.map(priceInterval => itineraryService.calclutatePriceIntervals(priceInterval.id));

    Promise.all(requests).then(res => {
      const prices = res.map(r => r.data);
      commit(C.ITINERARY_MATRIX_CALC_PRICES_SUCCESS, prices);
      dispatch('snackbar/show', {
        identifier: 'success',
        message: 'Successfully calculated matrix prices',
      }, { root: true });
    }).catch(e => {
      commit(C.ITINERARY_MATRIX_CALC_PRICES_FAILURE);
      dispatch('snackbar/show', {
        identifier: 'error',
        message: `Failed to calculate matrix prices. - ${e.response.data.data}`,
      }, { root: true });
    });
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   */
  setAllMatrixWithCalculated({ commit, getters, dispatch }) {
    commit(C.ITINERARY_SET_ALL_PRICES_PENDING);
    const mappedMatrixPrices = getters.getMappedMatrixPrices;

    mappedMatrixPrices.forEach(matrixPrice => {
      matrixPrice.prices.forEach(price => {
        dispatch('setMatrixPrice', { price, newPrice: price.calculatedPrice, format: true });
      });
    });
    commit(C.ITINERARY_SET_ALL_PRICES_SUCCESS);
  },

  /**
   * @param commit
   * @param state
   * @param price
   * @param newPrice
   * @param format
   */
  setMatrixPrice({ commit, state }, { price, newPrice, format }) {
    const matrixPrices = cloneDeep(state.matrixPrices.items);

    matrixPrices.forEach(matrixPrice => {
      const priceIndex = matrixPrice.prices.findIndex(matricPricesPrice =>
        matricPricesPrice.itineraryMatrixAgeId === price.itineraryMatrixAgeId &&
        matricPricesPrice.itineraryPriceIntervalId === price.itineraryPriceIntervalId);

      if (priceIndex !== -1) {
        matrixPrice.prices[priceIndex].price = format ? Math.ceil(newPrice / 10) * 10 : newPrice;
      }
    });

    commit(C.ITINERARY_UPDATE_MATRIX_PRICES, matrixPrices);
  },

  /**
   * @param commit
   * @param state
   * @param matrixIndex
   */
  setMatrixActiveStatus({ commit, state }, matrixIndex) {
    const matrixPrices = cloneDeep(state.matrixPrices.items);

    matrixPrices[matrixIndex].enabled = !matrixPrices[matrixIndex].enabled;
    commit(C.ITINERARY_UPDATE_MATRIX_ENABLED_STATUS, matrixPrices);
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   * @param itineraryId
   * @param domainId
   */
  saveMatrixPrices({ commit, state, dispatch }, { itineraryId, domainId }) {
    commit(C.ITINERARY_SAVE_MATRIX_PRICES_PENDING);
    const matrixPrices = state.matrixPrices.items;

    return itineraryService.saveMatrixPrices({ itineraryId, domainId, matrixPrices }).then(res => {
      if (res.status === 204) {
        commit(C.ITINERARY_SAVE_MATRIX_PRICES_SUCCESS);
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'Successfully saved matrix prices',
        }, { root: true });
      } else {
        throw new Error();
      }
    }).catch(() => {
      commit(C.ITINERARY_SAVE_MATRIX_PRICES_FAILURE);
      dispatch('snackbar/show', {
        identifier: 'error',
        message: 'Failed to save matrix prices',
      }, { root: true });
    });
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   * @param itineraryId
   * @param domainId
   * @param priceIntervalId
   */
  updateCalculatedPricesForInterval({ commit, state, dispatch }, { itineraryId, domainId, priceIntervalId }) {
    const pricesintervals = state.priceIntervals.items;
    const priceIntervalIndex = pricesintervals.findIndex(priceInterval => priceInterval.id === priceIntervalId);
    const calculatedMatrixPrices = cloneDeep(state.calculatedMatrixPrices.items);

    return itineraryService.calclutatePriceIntervals(priceIntervalId).then(res => {
      calculatedMatrixPrices[priceIntervalIndex] = res.data;
      commit(C.ITINERARY_MATRIX_CALC_PRICES_SUCCESS, calculatedMatrixPrices);
      dispatch('updateMatrixPricesForPriceInterval', { itineraryId, domainId, priceIntervalIndex });
    }).catch(e => {
      dispatch('snackbar/show', {
        identifier: 'error',
        message: `Failed to update matrix prices. - ${e.response.data.data}`,
      }, { root: true });
    });
  },

  /**
   * @param dispatch
   * @param getters
   * @param payload
   */
  updateMatrixPricesForPriceInterval({ getters, dispatch }, payload) {
    const mappedMatrixPrices = getters.getMappedMatrixPrices;

    mappedMatrixPrices[payload.priceIntervalIndex].prices.forEach(price => {
      dispatch('setMatrixPrice', { price, newPrice: price.calculatedPrice });
    });

    dispatch('saveMatrixPrices', payload);
  },

  getItineraryTexts({ commit }, { itineraryId, domainId }) {
    commit(C.ITINERARY_GET_ITINERARY_TEXTS_PENDING);

    return itineraryService.getItineraryTexts({ itineraryId, domainId }).then(res => {
      commit(C.ITINERARY_GET_ITINERARY_TEXTS_SUCCESS, res.data);
    }).catch(() => {
      commit(C.ITINERARY_GET_ITINERARY_TEXTS_FAILURE);
    });
  },

  saveItineraryTexts({ state, dispatch }, { itineraryId, domainId }) {
    let texts = cloneDeep(state.itineraryTexts.items);
    texts = texts.filter(text => text.text);

    return itineraryService.saveItineraryTexts({ itineraryId, domainId, texts }).then(() => {
      dispatch('snackbar/show', {
        identifier: 'success',
        message: 'Successfully saved itinerary texts',
      }, { root: true });
    }).catch(() => {
      dispatch('snackbar/show', {
        identifier: 'error',
        message: 'Failed to update itinerary texts',
      }, { root: true });
    });
  },

  updateItineraryText({ commit, state }, { text, value }) {
    const texts = cloneDeep(state.itineraryTexts.items);

    // find text types
    const types = texts.filter(t => t.type === text.type);

    if (text.type === 'main') {
      if (!types.length) {
        types.push({ ...text, text: value });
      } else {
        types[0].text = value;
      }
    } else if (!types.length) {
      types.push({ ...text, text: value });
    } else {
      types[text.index].text = value;
    }

    const texters = texts.filter(t => t.type !== text.type);
    commit(C.ITINERARY_UPDATE_ITINERARY_TEXT, [ ...texters, ...types ]);
  },

  addItineraryText({ commit, state }, type) {
    const texts = cloneDeep(state.itineraryTexts.items);

    texts.push({
      type,
      text: '',
    });

    commit(C.ITINERARY_UPDATE_ITINERARY_TEXT, texts);
  },

  copyDayProgramEntry({ dispatch, state, commit }, payload) {
    return new Promise((resolve, reject) => {
      const dayProgramEntry = payload.dayProgramEntry;
      const itineraryId = payload.itineraryId;

      const siteIds = dayProgramEntry.hotels.map(hotel => hotel.siteId);
      const extras = dayProgramEntry.extras;

      // Get associated dayProgramDomain if a domain is selected
      let dayProgramDomain = null;
      if (state.domains.selected) {
        dayProgramDomain = state.domains.selected.dayPrograms.find(
          selectedDomainDayProgram => selectedDomainDayProgram.itineraryDayProgramId === dayProgramEntry.id
        );
      }
      const dayTextPayload = {
        dayText: {
          teaserText: dayProgramEntry.teaserText,
          text: dayProgramEntry.text,
        },
        dayTextDomain: dayProgramDomain ? {
          teaserText: dayProgramDomain.teaserText,
          text: dayProgramDomain.text,
        } : null,
      };
      const filteredDayProgramList = [ ...state.dayProgram.items ].filter(
        dayProgram =>
          dayProgram.areaId === dayProgramEntry.areaId
          && dayProgram.description === dayProgramEntry.description
          && dayProgram.id !== dayProgramEntry.id,
      );

      const promises = filteredDayProgramList.map(dayProgram => {
        const dayProgramId = dayProgram.id;
        const sitesPromise = dispatch('updateDayProgramSites', {
          itineraryId, dayProgramId, siteIds,
        });
        const extrasPromise = dispatch('updateDayProgramExtras', {
          itineraryId, dayProgramId, extras,
        });
        const mealPlansPromise = dispatch('getDayProgramMealPlans', {
          itineraryId, data: dayProgramEntry,
        }).then(() => {
          const dayProgramMeals = state.dayProgramMealPlans.mealPlans;
          return dispatch('updateDayProgramMealPlans', {
            itineraryId, dayProgramId, mappings: dayProgramMeals,
          });
        });

        const dayTextPromise = dispatch('updateDayProgramDayText', {
          itineraryId,
          dayProgramId,
          data: dayTextPayload,
        });
        return Promise.all([ sitesPromise, extrasPromise, mealPlansPromise, dayTextPromise ]).then(results => results);
      });

      // Set total amount of loading steps
      commit('app/APP_LOADING_PENDING', { total: promises.length, current: 0 }, { root: true });

      /*
        Each promises represents a dayprogram entry being updated
        The following code ensures that each promise waits for the last one to be fully updated
        before executing the next promise
      */

      let sequence = Promise.resolve();
      promises.forEach((promise, index) => {
        sequence = sequence.then(() => {
          commit('app/APP_LOADING_SET_CURRENT_STEP', index + 1, { root: true });
          return promise;
        });
      });

      Promise.all(promises)
        .then(() => {
          dispatch('snackbar/show', {
            identifier: 'success',
            message: 'Successfully copied day program entry',
          }, { root: true });
          commit('app/APP_LOADING_SUCCESS', null, { root: true });
          dispatch('getDayProgram', payload.itineraryId);
          dispatch('getItineraryDomains', payload.itineraryId).then(res => {
            if (state.domains.selected) {
              const newSelectedDomain = res.find(itineraryDomain => itineraryDomain.id === state.domains.selected.id);
              dispatch('selectItineraryDomain', newSelectedDomain);
            }
          });
          resolve();
        })
        .catch(() => {
          dispatch('snackbar/show', {
            identifier: 'error',
            message: 'Failed to copy day program entry. Contact IT.',
          }, { root: true });
          commit('app/APP_LOADING_ERROR', null, { root: true });
          reject();
        });
    });
  },

  updateDayProgramDayText({ dispatch, state }, payload) {
    const selectedDomain = state.domains.selected;
    return itineraryService.updateDayProgramDayText(
      payload.itineraryId,
      payload.dayProgramId,
      selectedDomain ? selectedDomain.id : null,
      payload.data,
    ).then(() => {
      dispatch('snackbar/show', {
        identifier: 'success',
        message: 'Day program day text updated',
      }, { root: true });
      if (payload.refreshList) {
        dispatch('getDayProgram', payload.itineraryId);
        dispatch('getItineraryDomains', payload.itineraryId).then(res => {
          if (state.domains.selected) {
            const newSelectedDomain = res.find(itineraryDomain => itineraryDomain.id === state.domains.selected.id);
            dispatch('selectItineraryDomain', newSelectedDomain);
          }
        });
      }
    }).catch(() => {
      dispatch('snackbar/show', {
        identifier: 'error',
        message: 'Failed to update day program day text',
      }, { root: true });
    });
  },

  /**
   * @param commit
   */
  getCarRentalSuppliers({ commit }) {
    commit(C.ITINERARY_CAR_RENTAL_SUPPLIERS_PENDING);
    itineraryService.getCarRentalSuppliers()
      .then(res => {
        commit(C.ITINERARY_CAR_RENTAL_SUPPLIERS_SUCCESS, res.data);
      }).catch(() => {
        commit(C.ITINERARY_CAR_RENTAL_SUPPLIERS_FAILURE);
      });
  },

  /**
   * @param commit
   * @param supplierId
   * @param startDate
   */
  getCarRentalSheetsBySupplierIdAndDate({ commit }, supplierId, startDate) {
    commit(C.ITINERARY_CAR_RENTAL_SHEETS_PENDING);
    return itineraryService.getCarRentalSheetsBySupplierIdAndDate(supplierId, startDate)
      .then(res => {
        commit(C.ITINERARY_CAR_RENTAL_SHEETS_SUCCESS, res.data);
      }).catch(() => {
        commit(C.ITINERARY_CAR_RENTAL_SHEETS_FAILURE);
      });
  },

  /**
   * @param commit
   * @param rentalId
   * @param startDate
   */
  getCarRentalGroupsByDate({ commit }, rentalId, startDate) {
    commit(C.ITINERARY_CAR_RENTAL_GROUPS_PENDING);
    return itineraryService.getCarRentalGroupsByDate(rentalId, startDate)
      .then(res => {
        commit(C.ITINERARY_CAR_RENTAL_GROUPS_SUCCESS, res.data);
      }).catch(() => {
        commit(C.ITINERARY_CAR_RENTAL_GROUPS_FAILURE);
      });
  },

  /**
   * @param dispatch
   * @param priceRequest
   * @param priceIntervalId
   */
  async addCarRentalPriceInterval({ dispatch }, { priceRequest, priceIntervalId }) {
    return itineraryService.addCarRentalPriceInterval(priceRequest).then(() => {
      dispatch('snackbar/show', {
        identifier: 'success',
        message: 'Successfully added price interval',
      }, { root: true });

      dispatch('getCarRentalPriceIntervalsByIntervalId', priceIntervalId);
    }).catch(() => {
      dispatch('snackbar/show', {
        identifier: 'error',
        message: 'Failed to add price interval',
      }, { root: true });
    });
  },

  /**
   * @param dispatch
   * @param id
   * @param priceIntervalId
   */
  copyCarRentalPriceInterval({ dispatch }, { id, priceIntervalId }) {
    return itineraryService.copyCarRentalPriceInterval(id).then(() => {
      dispatch('snackbar/show', {
        identifier: 'success',
        message: 'Successfully copied price interval',
      }, { root: true });

      dispatch('getCarRentalPriceIntervalsByIntervalId', priceIntervalId);
    }).catch(() => {
      dispatch('snackbar/show', {
        identifier: 'error',
        message: 'Failed to copy price interval',
      }, { root: true });
    });
  },

  /**
   * @param dispatch
   * @param id
   * @param priceIntervalId
   */
  deleteCarRentalPriceInterval({ dispatch }, { id, priceIntervalId }) {
    return itineraryService.deleteCarRentalPriceInterval(id).then(() => {
      dispatch('snackbar/show', {
        identifier: 'success',
        message: 'Successfully deleted price interval',
      }, { root: true });

      dispatch('getCarRentalPriceIntervalsByIntervalId', priceIntervalId);
    }).catch(() => {
      dispatch('snackbar/show', {
        identifier: 'error',
        message: 'Failed to delete price interval',
      }, { root: true });
    });
  },

  /**
   * @param commit
   * @param priceIntervalId
   */
  getCarRentalPriceIntervalsByIntervalId({ commit }, priceIntervalId) {
    commit(C.ITINERARY_GET_CAR_RENTAL_PENDING);
    return itineraryService.getCarRentalPriceIntervalsByIntervalId(priceIntervalId)
      .then(res => {
        commit(C.ITINERARY_GET_CAR_RENTAL_SUCCESS, res.data);
      }).catch(() => {
        commit(C.ITINERARY_GET_CAR_RENTAL_FAILURE);
      });
  },

  updateCarRentalCommissionRatio({ state, commit }, { id, commissionRatio }) {
    const carRentals = cloneDeep(state.carRentals.items);

    const carRentalIndex = carRentals.findIndex(carRental => carRental.id === id);
    carRentals[carRentalIndex].commissionRatio = +commissionRatio;

    commit(C.ITINERARY_CAR_RENTAL_UPDATE_COMMISSION_RATIO, carRentals);
  },

  updateCarRentalContributionRatio({ state, commit }, { id, contributionRatio }) {
    const carRentals = cloneDeep(state.carRentals.items);

    const carRentalIndex = carRentals.findIndex(carRental => carRental.id === id);
    carRentals[carRentalIndex].contributionRatio = +contributionRatio;

    commit(C.ITINERARY_CAR_RENTAL_UPDATE_CONTRIBUTION_RATIO, carRentals);
  },

  calculateCarRental({ state, commit, dispatch }) {
    commit(C.ITINERARY_CAR_RENTAL_CALC_PRICE_PENDING);

    const carRentals = cloneDeep(state.carRentals.items);

    carRentals.forEach(carRental => itineraryService.calculateCarRental({
      carRentalIntervalId: carRental.id,
      commissionRatio: carRental.commissionRatio,
      contributionRatio: carRental.contributionRatio,
    }).then(res => {
      dispatch('snackbar/show', {
        identifier: 'success',
        message: 'Successfully calculated price',
      }, { root: true });

      const carRentalIndex = carRentals.findIndex(cr => cr.id === carRental.id);
      carRentals[carRentalIndex] = res.data;

      commit(C.ITINERARY_CAR_RENTAL_CALC_PRICE_SUCCESS, carRentals);

      /* dispatch(); */
    }).catch(() => {
      commit(C.ITINERARY_CAR_RENTAL_CALC_PRICE_FAILURE);
      dispatch('snackbar/show', {
        identifier: 'error',
        message: 'Failed to add price interval',
      }, { root: true });
    }));
  },

  changePriceInterval({ state, commit, dispatch }, priceIntervalId) {
    commit(C.ITINERARY_SELECT_PRICE_INTERVAL_INDEX, priceIntervalId);

    if (priceIntervalId) {
      dispatch('getItineraryPriceSettings', priceIntervalId);
      dispatch('getCarRentalPriceIntervalsByIntervalId', priceIntervalId);
    }
  },

  resetDataForDomainChange({ commit }) {
    commit(C.ITINERARY_PRICE_INTERVALS_GET_SUCCESS, []);
    commit(C.ITINERARY_PRICE_SETTINGS_GET_SUCCESS, []);
    commit(C.ITINERARY_GET_CAR_RENTAL_SUCCESS, []);
  },

  /**
   * @param commit
   * @param itineraryId
   * @param domainId
   * @returns {Promise<T>}
   */
  getItineraryDomainPoster({ commit }, { itineraryId, domainId }) {
    commit(C.ITINERARY_POSTER_PENDING);
    return itineraryService.getItineraryDomainPoster(itineraryId, domainId)
      .then(res => {
        if (res.status === 200) {
          commit(C.ITINERARY_POSTER_SUCCESS, res.data);
        } else if (res.status === 204) {
          commit(C.ITINERARY_POSTER_NO_CONTENT);
        }
      }).catch(() => {
        commit(C.ITINERARY_POSTER_FAILURE);
      });
  },

  createItineraryDomainPoster({ commit, dispatch }, { itineraryId, domainId }) {
    commit(C.ITINERARY_POSTER_CREATE_PENDING);
    return itineraryService.createItineraryDomainPoster(itineraryId, domainId)
      .then(res => {
        commit(C.ITINERARY_POSTER_CREATE_SUCCESS, res.data);
        dispatch('getItineraryDomainPosterImages', { itineraryId, domainId });
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'Created itinerary domain poster',
        }, { root: true });
      }).catch(err => {
        commit(C.ITINERARY_POSTER_CREATE_FAILURE, err.response.data.data);
      });
  },

  updateItineraryDomainPoster({ commit, dispatch }, payload) {
    commit(C.ITINERARY_POSTER_UPDATE_PENDING);
    return itineraryService.updateItineraryDomainPoster(payload)
      .then(res => {
        commit(C.ITINERARY_POSTER_UPDATE_SUCCESS, res.data);
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'Updated itinerary domain poster',
        }, { root: true });
      }).catch(() => {
        commit(C.ITINERARY_POSTER_UPDATE_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'Failed to update itinerary domain poster',
        }, { root: true });
    });
  },

  /**
   * @param commit
   * @param itineraryId
   * @param domainId
   * @returns {Promise<T>}
   */
  getItineraryDomainPosterImages({ commit }, { itineraryId, domainId }) {
    commit(C.ITINERARY_POSTER_IMAGES_PENDING);
    return itineraryService.getItineraryDomainPosterImages(itineraryId, domainId)
      .then(res => {
        if (res.status === 200) {
          commit(C.ITINERARY_POSTER_IMAGES_SUCCESS, res.data);
        } else if (res.status === 204) {
          commit(C.ITINERARY_POSTER_IMAGES_NO_CONTENT);
        }
      }).catch(() => {
        commit(C.ITINERARY_POSTER_IMAGES_FAILURE);
      });
  },

  updateItineraryDomainPosterImages({ commit }, payload) {
    commit(C.ITINERARY_POSTER_IMAGES_UPDATE_PENDING);
    return itineraryService.updateItineraryDomainPosterImages(payload)
      .then(res => {
        if (res.status === 200) {
          commit(C.ITINERARY_POSTER_IMAGES_UPDATE_SUCCESS, res.data);
        }
      }).catch(() => {
        commit(C.ITINERARY_POSTER_IMAGES_UPDATE_FAILURE);
      });
  },

  uploadItineraryDomainPosterImages({ commit }, payload) {
    commit(C.ITINERARY_POSTER_IMAGES_UPLOAD_PENDING);
    return itineraryService.uploadItineraryDomainPosterImages(payload)
      .then(res => {
        if (res.status === 200) {
          commit(C.ITINERARY_POSTER_IMAGES_UPLOAD_SUCCESS, res.data);
        }
      }).catch(() => {
        commit(C.ITINERARY_POSTER_IMAGES_UPLOAD_FAILURE);
      });
  },

  generateItineraryDomainPosterQrCode({ commit }, payload) {
    commit(C.ITINERARY_POSTER_QR_CODE_PENDING);
    return itineraryService.generateItineraryDomainPosterQrCode(payload)
      .then(res => {
        if (res.status === 200) {
          commit(C.ITINERARY_POSTER_QR_CODE_SUCCESS, res.data);
        }
      }).catch(() => {
        commit(C.ITINERARY_POSTER_QR_CODE_FAILURE);
      });
  },

  setSelectedExtraCategory({ commit }, payload) {
    commit(C.ITINERARY_DAY_PROGRAM_SELECTED_EXTRA_CATEGORY, payload);
  },

  getItinerarySuppliers({ commit }, { itineraryId }) {
    commit(C.ITINERARY_SUPPLIERS_SYNC_PENDING);
    return itineraryService.getItinerarySuppliers(itineraryId)
      .then(res => {
        if (res.status === 200) {
          commit(C.ITINERARY_SUPPLIERS_SYNC_SUCCESS, res.data);
        } else if (res.status === 204) {
          commit(C.ITINERARY_SUPPLIERS_SYNC_NO_CONTENT);
        }
      }).catch(() => {
        commit(C.ITINERARY_SUPPLIERS_SYNC_FAILURE);
      });
  },
};



// WEBPACK FOOTER //
// ./src/store/modules/itinerary/actions.js